import { Fragment, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  SentimentSatisfiedAlt,
  SentimentNeutral,
  SentimentDissatisfied,
  Search,
} from "@mui/icons-material";
import { formatDateTime } from "@nomnom/core";
import type { Feedback as FeedbackType, FeedbackMood } from "../types/firestore";
import { subscribeToFeedback } from "../services/feedbackService";

type RoleFilter = "all" | "student" | "rider";
type MoodFilter = "all" | FeedbackMood;

function getMoodIcon(mood?: FeedbackMood) {
  switch (mood) {
    case "happy":
      return <SentimentSatisfiedAlt color="success" />;
    case "neutral":
      return <SentimentNeutral color="warning" />;
    case "frustrated":
      return <SentimentDissatisfied color="error" />;
    default:
      return <Typography color="text.secondary">—</Typography>;
  }
}

function getRoleColor(role: string): "primary" | "secondary" {
  return role === "student" ? "primary" : "secondary";
}

export default function Feedback() {
  const [feedback, setFeedback] = useState<FeedbackType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<RoleFilter>("all");
  const [moodFilter, setMoodFilter] = useState<MoodFilter>("all");
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);

  useEffect(() => {
    return subscribeToFeedback(
      (items) => {
        setFeedback(items);
        setLoading(false);
        setError(null);
      },
      (message) => {
        setLoading(false);
        setError(message);
      },
    );
  }, []);

  const filtered = useMemo(() => {
    let result = feedback;

    if (roleFilter !== "all") {
      result = result.filter((f) => f.userRole === roleFilter);
    }
    if (moodFilter !== "all") {
      result = result.filter((f) => f.mood === moodFilter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (f) =>
          f.message.toLowerCase().includes(q) ||
          (f.userDisplayName ?? "").toLowerCase().includes(q) ||
          (f.userEmail ?? "").toLowerCase().includes(q),
      );
    }

    return result;
  }, [feedback, roleFilter, moodFilter, search]);

  const stats = useMemo(() => {
    const total = feedback.length;
    const happy = feedback.filter((f) => f.mood === "happy").length;
    const neutral = feedback.filter((f) => f.mood === "neutral").length;
    const frustrated = feedback.filter((f) => f.mood === "frustrated").length;
    return { total, happy, neutral, frustrated };
  }, [feedback]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Feedback
      </Typography>

      {/* Stat Cards */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="h5">{stats.total}</Typography>
            <Typography color="text.secondary">Total</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="h5" color="success.main">
              {stats.happy}
            </Typography>
            <Typography color="text.secondary">Happy</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="h5" color="warning.main">
              {stats.neutral}
            </Typography>
            <Typography color="text.secondary">Neutral</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="h5" color="error.main">
              {stats.frustrated}
            </Typography>
            <Typography color="text.secondary">Frustrated</Typography>
          </CardContent>
        </Card>
      </Stack>

      {/* Filters */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <TextField
          size="small"
          placeholder="Search feedback..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <Search sx={{ mr: 1, color: "text.secondary" }} /> }}
          sx={{ flex: 1 }}
        />
        <TextField
          select
          size="small"
          label="Role"
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value as RoleFilter)}
          sx={{ minWidth: 120 }}
        >
          <MenuItem value="all">All Roles</MenuItem>
          <MenuItem value="student">Student</MenuItem>
          <MenuItem value="rider">Rider</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          label="Mood"
          value={moodFilter}
          onChange={(e) => setMoodFilter(e.target.value as MoodFilter)}
          sx={{ minWidth: 120 }}
        >
          <MenuItem value="all">All Moods</MenuItem>
          <MenuItem value="happy">Happy</MenuItem>
          <MenuItem value="neutral">Neutral</MenuItem>
          <MenuItem value="frustrated">Frustrated</MenuItem>
        </TextField>
      </Stack>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Mood</TableCell>
              <TableCell>Message</TableCell>
              <TableCell>Screen</TableCell>
              <TableCell>Order ID</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((item) => {
              const isExpanded = expandedRowId === item.id;
              return (
                <Fragment key={item.id}>
                  <TableRow
                    hover
                    onClick={() =>
                      setExpandedRowId(isExpanded ? null : item.id)
                    }
                    sx={{ cursor: "pointer", "& > *": { borderBottom: isExpanded ? "unset" : undefined } }}
                  >
                    <TableCell>
                      {item.createdAt ? formatDateTime(item.createdAt) : "—"}
                    </TableCell>
                    <TableCell>{item.userDisplayName ?? "—"}</TableCell>
                    <TableCell>{item.userEmail ?? "—"}</TableCell>
                    <TableCell>
                      <Chip
                        label={item.userRole}
                        size="small"
                        color={getRoleColor(item.userRole)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{getMoodIcon(item.mood)}</TableCell>
                    <TableCell>
                      {item.message.length > 100
                        ? `${item.message.slice(0, 100)}...`
                        : item.message}
                    </TableCell>
                    <TableCell>{item.screen ?? "—"}</TableCell>
                    <TableCell>
                      {item.orderId
                        ? item.orderId.slice(0, 8) + "..."
                        : "—"}
                    </TableCell>
                  </TableRow>
                  {isExpanded ? (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ py: 0 }}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Full Message
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 2, whiteSpace: "pre-wrap" }}>
                              {item.message}
                            </Typography>
                            <Stack direction="row" spacing={4}>
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  App Version
                                </Typography>
                                <Typography variant="body2">
                                  {item.appVersion ?? "—"}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  User ID
                                </Typography>
                                <Typography variant="body2">
                                  {item.userId}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Order ID
                                </Typography>
                                <Typography variant="body2">
                                  {item.orderId ?? "—"}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Screen
                                </Typography>
                                <Typography variant="body2">
                                  {item.screen ?? "—"}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Mood
                                </Typography>
                                <Typography variant="body2">
                                  {item.mood ?? "—"}
                                </Typography>
                              </Box>
                            </Stack>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  ) : null}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {filtered.length === 0 && (
        <Box textAlign="center" py={4}>
          <Typography color="text.secondary">
            No feedback found matching your filters.
          </Typography>
        </Box>
      )}
    </Box>
  );
}
